import React from "react";
import { ChevronDown, ChevronUp } from "../../../components/icons/svg/outline";
import { formatDate } from "../../../utils/datetime";
import PayrollStatusChips from "./PayrollStatusChips";
import PayoutTimesheetCompactTable from "./PayoutTimesheetCompactTable";
import { formatDashboardCount, formatDashboardHours } from "./summaryUtils";

/**
 * Displays one collapsible historical payroll week section.
 *
 * @param {Object} props - Component props.
 * @param {Object} props.week - Week dashboard data.
 * @param {boolean} props.isExpanded - Whether the week rows are visible.
 * @param {Function} props.onToggle - Expand/collapse handler.
 * @param {Function} props.onSelect - Row click handler.
 * @returns {JSX.Element} Payroll week section.
 */
const PayrollWeekSection = ({ week, isExpanded, onToggle, onSelect }) => {
  const Icon = isExpanded ? ChevronUp : ChevronDown;
  const summary = week.summary || {};
  const hasSummaryValue = (value) => Number(value) > 0;

  return (
    <div className="overflow-x-auto">
      <section className="min-w-[800px] overflow-hidden rounded-lg border border-white/70 bg-white shadow-lg shadow-primary-900/10">
        <button
          type="button"
          className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left outline-none transition hover:bg-gray-50 focus:outline-none focus:ring-0"
          onClick={() => onToggle(week.weekEnding)}
        >
          <div className="min-w-0 flex-1 space-y-2">
            <div>
              <h3 className="text-base font-bold text-gray-950">
                Week Ending {formatDate(week.weekEnding, "d mon DD, YYYY")}
              </h3>
            </div>
            <PayrollStatusChips statuses={week.statuses} />
            <div className="flex flex-wrap gap-2 text-[11px] text-gray-600">
              {summary.pendingCount ? (
                <span className="rounded-full font-bold bg-gray-100 px-2 py-0.5 text-gray-700">
                  {summary.pendingCount} Pending
                </span>
              ) : null}
              {summary.approvingCount ? (
                <span className="rounded-full font-bold bg-yellow-50 px-2 py-0.5 text-yellow-800">
                  {summary.approvingCount} Approving
                </span>
              ) : null}
              {summary.approvedCount ? (
                <span className="rounded-full font-bold bg-green-50 px-2 py-0.5 text-green-800">
                  {summary.approvedCount} Approved
                </span>
              ) : null}
              {summary.exportedCount ? (
                <span className="rounded-full font-bold bg-green-50 px-2 py-0.5 text-green-900">
                  {summary.exportedCount} Exported
                </span>
              ) : null}
              {summary.editedCount ? (
                <span className="rounded-full font-bold bg-orange-50 px-2 py-0.5 text-orange-800">
                  {summary.editedCount} Edited
                </span>
              ) : null}
              {hasSummaryValue(summary.totalWorkerHours) ? (
                <span className="rounded-full bg-gray-100 px-2 py-0.5">
                  {formatDashboardHours(summary.totalWorkerHours)} Worker Hours
                </span>
              ) : null}
              {hasSummaryValue(summary.totalHoursLogHours) ? (
                <span className="rounded-full bg-gray-100 px-2 py-0.5">
                  {formatDashboardHours(summary.totalHoursLogHours)} Manager Hours
                </span>
              ) : null}
              {hasSummaryValue(summary.totalPayoutHours) ? (
                <span className="rounded-full bg-gray-100 px-2 py-0.5">
                  {formatDashboardHours(summary.totalPayoutHours)} Payout Hours
                </span>
              ) : null}
              {hasSummaryValue(summary.totalShifts) ? (
                <span className="rounded-full bg-gray-100 px-2 py-0.5">
                  {formatDashboardCount(summary.totalShifts)} Shifts
                </span>
              ) : null}
              {hasSummaryValue(summary.totalProjects) ? (
                <span className="rounded-full bg-gray-100 px-2 py-0.5">
                  {formatDashboardCount(summary.totalProjects)} Projects
                </span>
              ) : null}
            </div>
          </div>
          <Icon className="h-5 w-5 flex-shrink-0 text-gray-400" />
        </button>

        {isExpanded ? (
          <PayoutTimesheetCompactTable
            timesheets={week.timesheets || []}
            onSelect={onSelect}
            quickBooksExported={week.statuses?.iifExported}
            emptyMessage="No payout timesheets found for this week."
          />
        ) : null}
      </section>
    </div>
  );
};

export default PayrollWeekSection;
